/**
 * Help Page - Mobile Version
 *
 * Mobile help page with navigation drawer and content display.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ManualContent } from '../../components/help/ManualContent';
import { MobileManualNav } from '../../components/help/MobileManualNav';
import { MobileHeader } from '../../components/mobile/MobileHeader';
import { MANUALS_METADATA, getAvailableManuals } from '../../data/manuals';
import type { TableOfContentItem, ManualMetadata } from '../../types/manual';
import { useAuth } from '../../contexts/AuthContext';
import styles from './MobileHelpPage.module.css';

export const MobileHelpPage = () => {
  const { user } = useAuth();
  const { manualId } = useParams<{ manualId?: string }>();
  const navigate = useNavigate();

  const [selectedManual, setSelectedManual] = useState<ManualMetadata | null>(null);
  const [content, setContent] = useState<string>('');
  const [tableOfContents, setTableOfContents] = useState<TableOfContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [navOpen, setNavOpen] = useState(false);

  const availableManuals = getAvailableManuals(user?.role || 'member');

  // Initialize selected manual from URL or default
  useEffect(() => {
    if (availableManuals.length === 0) {
      setError('沒有可用的操作手冊');
      setLoading(false);
      return;
    }

    const manualToUse = manualId
      ? MANUALS_METADATA.find((m) => m.id === manualId)
      : availableManuals[0];

    if (!manualToUse || !availableManuals.find((m) => m.id === manualToUse.id)) {
      navigate(`/help/${availableManuals[0].id}`, { replace: true });
      return;
    }

    setSelectedManual(manualToUse);
  }, [manualId, availableManuals, navigate]);

  // Load manual content
  useEffect(() => {
    if (!selectedManual) return;

    const loadManual = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(selectedManual.path);

        if (!response.ok) {
          throw new Error('無法載入手冊內容');
        }

        const text = await response.text();
        setContent(text);
      } catch (err) {
        setError(err instanceof Error ? err.message : '載入失敗');
        setContent('');
      } finally {
        setLoading(false);
      }
    };

    loadManual();
  }, [selectedManual]);

  const handleManualChange = (manualId: string) => {
    navigate(`/help/${manualId}`);
  };

  const handleMenuClick = () => {
    setNavOpen(true);
  };

  const handleNavClose = () => {
    setNavOpen(false);
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <MobileHeader title="操作手冊" showMenu onMenu={handleMenuClick} />
        <div className={styles.loading}>載入中...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.page}>
        <MobileHeader title="操作手冊" showMenu onMenu={handleMenuClick} />
        <div className={styles.error}>
          <p>⚠️ {error}</p>
        </div>
      </div>
    );
  }

  if (!selectedManual) {
    return null;
  }

  return (
    <div className={styles.page}>
      <MobileHeader
        title={selectedManual.title}
        showBack={false}
        showMenu
        onMenu={handleMenuClick}
      />

      <MobileManualNav
        availableManuals={availableManuals}
        selectedManual={selectedManual}
        tableOfContents={tableOfContents}
        onManualChange={handleManualChange}
        onClose={handleNavClose}
        isOpen={navOpen}
      />

      <div className={styles.content}>
        <ManualContent
          content={content}
          onTableOfContentsExtracted={setTableOfContents}
          basePath={selectedManual.path}
        />
      </div>
    </div>
  );
};

export default MobileHelpPage;
